import { useEffect, useRef, type ReactNode } from 'react';
import { generateText } from '@tiptap/core';
import { EditorContent, type Editor, type JSONContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Code2, Italic, List, ListOrdered, Strikethrough } from 'lucide-react';
import removeMarkdown from 'remove-markdown';
import { Markdown } from 'tiptap-markdown';

const legacyRichTextPrefix = '__futurelab_tiptap__:';

export function messageText(message: unknown): string {
	if (typeof message === 'string') return message;
	if (message == null) return '';
	if (typeof message === 'number' || typeof message === 'boolean') return String(message);
	if (typeof message !== 'object') return '';

	const record = message as Record<string, unknown>;
	for (const key of ['body', 'message', 'text', 'content']) {
		const nested = messageText(record[key]);
		if (nested) return nested;
	}
	return '';
}

export function decodeRichText(message?: unknown): JSONContent | null {
	const normalizedMessage = messageText(message);
	if (!normalizedMessage.startsWith(legacyRichTextPrefix)) return null;
	try {
		return JSON.parse(normalizedMessage.slice(legacyRichTextPrefix.length)) as JSONContent;
	} catch {
		return null;
	}
}

export function richTextPreview(message?: unknown) {
	const document = decodeRichText(message);
	return (document ? generateText(document, [StarterKit]) : removeMarkdown(messageText(message))).replace(/\s+/g, ' ').trim();
}

interface ChatRichTextEditorProps {
	value: string;
	onChange: (value: string) => void;
	onFocus: () => void;
	onSubmit: () => void;
}

interface FormattingButtonProps {
	active?: boolean;
	disabled?: boolean;
	label: string;
	onFormat: () => void;
	children: ReactNode;
}

function FormattingButton({ active, disabled, label, onFormat, children }: FormattingButtonProps) {
	return (
		<button
			aria-label={label}
			aria-pressed={active}
			className="inline-flex size-7 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-white hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 data-[active=true]:bg-white data-[active=true]:text-primary"
			data-active={active}
			disabled={disabled}
			type="button"
			onClick={onFormat}
			onMouseDown={(event) => event.preventDefault()}
		>
			{children}
		</button>
	);
}

export function ChatRichTextEditor({ value, onChange, onFocus, onSubmit }: ChatRichTextEditorProps) {
	const submitRef = useRef(onSubmit);
	const focusRef = useRef(onFocus);
	const changeRef = useRef(onChange);
	const editorRef = useRef<Editor | null>(null);
	submitRef.current = onSubmit;
	focusRef.current = onFocus;
	changeRef.current = onChange;

	const editor = useEditor({
		extensions: [
			StarterKit.configure({ blockquote: false, codeBlock: false, heading: false, horizontalRule: false }),
			Markdown.configure({ html: false, tightLists: true, transformCopiedText: true, transformPastedText: true }),
		],
		content: '',
		editorProps: {
			attributes: {
				'aria-label': 'Message',
				'aria-multiline': 'true',
				class: 'max-h-40 min-h-10 overflow-y-auto px-2 py-1.5 text-sm outline-none [&_code]:rounded [&_code]:bg-black/10 [&_code]:px-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:min-h-5 [&_ul]:list-disc [&_ul]:pl-5',
				role: 'textbox',
			},
			handleKeyDown: (_view, event) => {
				const currentEditor = editorRef.current;
				if (
					event.key === 'Enter' &&
					event.shiftKey &&
					(currentEditor?.isActive('bulletList') || currentEditor?.isActive('orderedList'))
				) {
					event.preventDefault();
					currentEditor.commands.splitListItem('listItem');
					return true;
				}
				if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
					event.preventDefault();
					submitRef.current();
					return true;
				}
				return false;
			},
		},
		onCreate: ({ editor: currentEditor }) => {
			editorRef.current = currentEditor;
		},
		onFocus: () => focusRef.current(),
		onUpdate: ({ editor: currentEditor }) => {
			changeRef.current(currentEditor.isEmpty ? '' : currentEditor.storage.markdown.getMarkdown());
		},
	});

	useEffect(() => {
		if (!value && editor && !editor.isEmpty) editor.commands.clearContent(false);
	}, [editor, value]);

	return (
		<div className="min-w-0 flex-1 rounded-2xl bg-slate-100 p-1.5 shadow-none focus-within:bg-white focus-within:ring-2 focus-within:ring-ring">
			<div aria-label="Message formatting" className="flex items-center gap-0.5 border-b border-slate-200 px-1 pb-1">
				<FormattingButton active={editor?.isActive('bold')} disabled={!editor?.can().chain().toggleBold().run()} label="Bold" onFormat={() => editor?.chain().focus().toggleBold().run()}><Bold className="size-4" /></FormattingButton>
				<FormattingButton active={editor?.isActive('italic')} disabled={!editor?.can().chain().toggleItalic().run()} label="Italic" onFormat={() => editor?.chain().focus().toggleItalic().run()}><Italic className="size-4" /></FormattingButton>
				<FormattingButton active={editor?.isActive('strike')} disabled={!editor?.can().chain().toggleStrike().run()} label="Strikethrough" onFormat={() => editor?.chain().focus().toggleStrike().run()}><Strikethrough className="size-4" /></FormattingButton>
				<FormattingButton active={editor?.isActive('code')} disabled={!editor?.can().chain().toggleCode().run()} label="Inline code" onFormat={() => editor?.chain().focus().toggleCode().run()}><Code2 className="size-4" /></FormattingButton>
				<FormattingButton active={editor?.isActive('bulletList')} disabled={!editor?.can().chain().toggleBulletList().run()} label="Bullet list" onFormat={() => editor?.chain().focus().toggleBulletList().run()}><List className="size-4" /></FormattingButton>
				<FormattingButton active={editor?.isActive('orderedList')} disabled={!editor?.can().chain().toggleOrderedList().run()} label="Numbered list" onFormat={() => editor?.chain().focus().toggleOrderedList().run()}><ListOrdered className="size-4" /></FormattingButton>
			</div>
			<div className="relative">
				{!value && <span aria-hidden="true" className="pointer-events-none absolute left-2 top-1.5 text-sm text-muted-foreground">Type a message</span>}
				<EditorContent editor={editor} />
			</div>
		</div>
	);
}
